'use client';

import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { SpinningLoader } from '@/app_components/icons/SpinningLoader';
import { SignInReadOnlyEmailInputV2 } from '@/app_components/SignIn/Components/SignInReadOnlyEmailInputV2';
import { SignInTitle } from '@/app_components/SignIn/Components/SignInSharedComponents';
import { SignInTermsAndMarketing } from '@/app_components/SignIn/Components/SignInTermsAndMarketing';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { Form, FormControl, FormField, FormItem } from '@/app_components/ui/form';
import { sendRegistrationConversionEvent } from '@/app_lib/services/Piano/pianoHelpers';
import type { SignInFlow } from '@/app_lib/types/accounts/signInFlow.entities';
import { type SignInStatus, UNEXPECTED_ERROR_MESSAGE, type UserConsentLocation } from '@/app_lib/types/accounts/signin';
import type { RegisterApiResponse } from '@/app_lib/types/accounts/signinApi';
import { getDeviceForTracking } from '@/app_lib/utils/deviceUtils';
import { getOnTrackEvent } from '@/app_lib/utils/signInFlowHelpers';
import { emailSchema } from '@/app_lib/utils/zod.helpers';

interface SignInCreateAccountFormProps {
  flow: SignInFlow;
  email: string;
  consentLocation: UserConsentLocation;
  onEditEmailClick: () => void;
  setSignInStatus: Dispatch<SetStateAction<SignInStatus>>;
  setDisableSocialLogins: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

export const SignInCreateAccountForm = ({
  flow,
  email,
  consentLocation,
  onEditEmailClick,
  disabled,
  setSignInStatus,
  setDisableSocialLogins,
}: SignInCreateAccountFormProps) => {
  const { track } = getOnTrackEvent(flow);
  const form = useForm({
    defaultValues: {
      email,
      registrationConsent: consentLocation === 'UK_EEA' || consentLocation === 'DEFAULT' || consentLocation === 'US',
    },
    reValidateMode: 'onSubmit',
  });

  const {
    formState: { isSubmitting, isSubmitSuccessful },
    setError,
  } = form;

  useEffect(() => {
    setDisableSocialLogins(isSubmitting || isSubmitSuccessful);
  }, [isSubmitting, isSubmitSuccessful, setDisableSocialLogins]);

  const onSubmit = async ({ registrationConsent }: { registrationConsent: boolean }) => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError('email', {
        message: parsed.error.issues[0].message,
        type: 'manual',
      });
      return;
    }

    try {
      const res = await fetch('/api/signin/register', {
        body: JSON.stringify({ email: parsed.data, registrationConsent, registrationSource: 'sign_in_flow' }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      const response = (await res.json()) as RegisterApiResponse;
      if (response.success) {
        sendRegistrationConversionEvent();
        localStorage.setItem('pendingSaveAuthMethod', 'registration');
        // AUTO-LOGIN: Re-enable this tracking when auto-login is restored in the register route.
        // trackEvent(
        //   {
        //     deviceType: getDeviceForTracking(),
        //     eventAction: 'login_attempt',
        //     loginMethod: 'Piano',
        //     loginSuccess: true,
        //     loginType: 'new_account',
        //     pianoUserId: response.pianoUid,
        //   },
        //   'signInEventTracker',
        // );
        setSignInStatus('PasswordlessNewAccount');
        return;
      }
      track({
        deviceType: getDeviceForTracking(),
        eventAction: 'login_attempt',
        loginMethod: 'Piano',
        loginSuccess: false,
        loginType: 'new_account',
      });
      setError('email', {
        message: response.message || UNEXPECTED_ERROR_MESSAGE,
        type: 'manual',
      });
    } catch {
      track({
        deviceType: getDeviceForTracking(),
        eventAction: 'login_attempt',
        loginMethod: 'Piano',
        loginSuccess: false,
        loginType: 'new_account',
      });
      setError('email', {
        message: UNEXPECTED_ERROR_MESSAGE,
        type: 'manual',
      });
    }
  };

  useEffect(() => {
    track({
      deviceType: getDeviceForTracking(),
      eventAction: 'account_signin_step_2', // is this the right event action? Seems to be the same as the successful pwless login event action.
      loginType: 'new_account',
    });
  }, [track]);

  return (
    <div className='flex flex-col items-center justify-center layout-gap-xs w-full'>
      <SignInTitle title='Create an account' />
      <Form {...form}>
        <form
          className='flex flex-col items-center justify-center layout-gap-xs w-full'
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className='flex w-full flex-col gap-sm'>
            <FormField
              control={form.control}
              name='email'
              render={({ field, fieldState }) => (
                <FormItem className='flex w-full flex-col space-y-0 gap-xs'>
                  <FormControl>
                    <SignInReadOnlyEmailInputV2
                      hasError={Boolean(fieldState.error)}
                      onChange={field.onChange}
                      onEditEmailClick={onEditEmailClick}
                      value={email}
                    />
                  </FormControl>
                </FormItem>
              )}
              rules={{
                required: 'Email is required',
              }}
            />
            {consentLocation && <SignInTermsAndMarketing consentLocation={consentLocation} />}
          </div>
          <ButtonV2
            as='button'
            className='w-full max-h-12'
            disabled={isSubmitSuccessful || isSubmitting || disabled}
            noCapitalize
            size='lg'
            type='submit'
            variant='primary'
          >
            {isSubmitting || isSubmitSuccessful ? <SpinningLoader /> : 'Create account'}
          </ButtonV2>
        </form>
      </Form>
    </div>
  );
};
